// Libraries
import { Flex, Stack, Text, useRadioGroup } from '@chakra-ui/react';
// Components
import { RatingRadio } from '../RatingRadio';
// Utils
import text from '@translations/en.json';
// Types
import { RatingKeys } from '@globalTypes/index';
import { Rating } from '@utils/index';
import { RatingPickerProps } from './RatingPicker.types';

/**
 * A component that provides a selection of different rating options.
 *
 * @component
 * @param {RatingPickerProps} props - The props for the RatingPicker component.
 * @param {boolean} [props.isDisabled=false] - Determines if the rating picker is disabled.
 * @param {string} props.name - The name attribute for the radio group.
 * @param {Function} props.onChange - Callback fired when the rating is changed.
 * @param {Record<RatingKeys, number>} props.optimizedSpeeds - Object mapping from rating keys to their corresponding optimized speeds.
 * @param {...FlexProps} props.flexProps - Additional props passed to the flex container.
 *
 * @returns {ReactElement} The RatingPicker component.
 */
export const RatingPicker = ({
  value,
  isDisabled = false,
  name,
  onChange,
  optimizedSpeeds,
  ...flexProps
}: RatingPickerProps) => {
  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    value: value ?? '',
    onChange,
  });
  const group = getRootProps();

  return (
    <Flex direction='column' gap={1} {...flexProps}>
      <Text fontSize='s' color='text.base'>
        {text.cii_simulator.buttons.controls.optimize_label}
      </Text>
      <Stack {...group} direction='row' gap={2} justify={{ base: 'center', md: 'start' }} py={1.5}>
        {(Object.keys(Rating) as RatingKeys[]).map((rating) => {
          const isRatingDisabled =
            Number(optimizedSpeeds[rating]?.ballastSpeedKts) < 0 ||
            Number(optimizedSpeeds[rating]?.ladenSpeedKts) < 0 ||
            isDisabled;
          const radio = getRadioProps({ value: rating });

          return <RatingRadio key={`optimize-${rating}`} {...{ isDisabled: isRatingDisabled, ...radio }} />;
        })}
      </Stack>
    </Flex>
  );
};
